/**
 * Ministry Map queries — private people tracker + connections + AI next-step.
 *
 * Admin-only at launch. Every call is scoped to the current user via RLS.
 *
 * Used by: MinistryMap page, PersonDrawer, ConnectionPicker, ArchiveSheet.
 */
import { supabase } from '@/lib/supabase'
import { invokeEdgeStream } from './_edge'
import { NEXT_STEP } from './_endpoints'
import { listForUser, insertOne, updateOneById } from './_helpers'
import { markEngagement } from './engagement'
import {
  ringForRelationship,
  RELATIONSHIP_RING_MAP,
  RELATIONSHIP_LABELS,
  CONNECTION_TYPE_LABELS,
} from '@/data/ministryConstants'

export { RELATIONSHIP_RING_MAP, RELATIONSHIP_LABELS, CONNECTION_TYPE_LABELS, ringForRelationship }

// ── People ──────────────────────────────────────────────────────

/** Load all active (non-archived) people for a user. */
export async function fetchPeople(userId) {
  return listForUser('ministry_people', {
    userId,
    filters: { archived: false },
    orderBy: 'created_at',
    ascending: true,
  })
}

/** Load archived people for a user. */
export async function fetchArchivedPeople(userId) {
  return listForUser('ministry_people', {
    userId,
    filters: { archived: true },
    orderBy: 'updated_at',
    ascending: false,
  })
}

/** Create a new person. Ring is auto-derived from relationship_type. */
export async function createPerson(userId, input) {
  const ring = ringForRelationship(input.relationship_type)
  const payload = {
    user_id: userId,
    first_name: input.first_name?.trim(),
    last_initial: input.last_initial?.trim() || null,
    relationship_type: input.relationship_type,
    ring,
    spiritual_status: input.spiritual_status || 'yellow',
    is_believer: !!input.is_believer,
    people_tags: input.people_tags || [],
    places: input.places || [],
    passions: input.passions || [],
    prayer_notes: input.prayer_notes?.trim() || null,
    next_step: input.next_step?.trim() || null,
    follow_up_date: input.follow_up_date || null,
    last_contact: input.last_contact || null,
  }
  const data = await insertOne('ministry_people', payload)
  markEngagement(userId)
  return data
}

/** Update a person. Ring re-derived if relationship_type changes. */
export async function updatePerson(personId, input) {
  const patch = { ...input }
  if (patch.relationship_type) patch.ring = ringForRelationship(patch.relationship_type)
  if (typeof patch.first_name === 'string') patch.first_name = patch.first_name.trim()
  if (typeof patch.last_initial === 'string') patch.last_initial = patch.last_initial.trim() || null
  if (typeof patch.prayer_notes === 'string') patch.prayer_notes = patch.prayer_notes.trim() || null
  if (typeof patch.next_step === 'string') patch.next_step = patch.next_step.trim() || null

  return updateOneById('ministry_people', personId, patch)
}

/** Soft delete (archive). */
export async function archivePerson(personId) {
  const { data, error } = await supabase
    .from('ministry_people')
    .update({ archived: true })
    .eq('id', personId)
    .select()
    .single()
  if (error) throw error
  return data
}

/** Restore from archive. */
export async function restorePerson(personId) {
  const { data, error } = await supabase
    .from('ministry_people')
    .update({ archived: false })
    .eq('id', personId)
    .select()
    .single()
  if (error) throw error
  return data
}

/** Hard delete — only callable from the archive view with confirmation. */
export async function deletePerson(personId) {
  const { error } = await supabase.from('ministry_people').delete().eq('id', personId)
  if (error) throw error
}

// ── Connections ─────────────────────────────────────────────────

/** Load all connections for a user (both active and archived included). */
export async function fetchConnections(userId) {
  const { data, error } = await supabase
    .from('ministry_connections')
    .select('*')
    .eq('user_id', userId)
  if (error) throw error
  return data || []
}

/** Create a connection between two people. Persons unordered — we sort to avoid duplicates. */
export async function createConnection(userId, personA, personB, type) {
  if (personA === personB) throw new Error('Cannot connect a person to themselves')
  const [a, b] = [personA, personB].sort()
  const { data, error } = await supabase
    .from('ministry_connections')
    .insert({ user_id: userId, person_a_id: a, person_b_id: b, connection_type: type })
    .select()
    .single()
  if (error) throw error
  return data
}

/** Delete a connection by ID. */
export async function deleteConnection(connectionId) {
  const { error } = await supabase.from('ministry_connections').delete().eq('id', connectionId)
  if (error) throw error
}

// ── AI next-step (streaming via edge function) ──────────────────

/**
 * Request an AI-suggested next step for a person.
 * Returns the raw Response for SSE streaming via parseSSEStream.
 * Only first name + last initial + status + tags + notes are sent — never other people.
 */
export async function fetchNextStep(personId, { signal } = {}) {
  return invokeEdgeStream(
    NEXT_STEP,
    { personId },
    { signal, errorMessage: 'Next-step request failed' }
  )
}
